import { Injectable, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { UserFile } from './models';
import { ApiService, CreateUserFilePayload, ReplaceUserFileContentPayload } from './api.service';

type FileAction = 'add' | 'save' | 'delete' | 'preview' | 'download' | 'upload';

export interface FilePreviewState {
  file: UserFile;
  filename: string;
  contentType: string;
  objectUrl: string;
  safeUrl: SafeResourceUrl;
  canEmbed: boolean;
}

@Injectable({ providedIn: 'root' })
export class FilesService {
  readonly files = signal<UserFile[]>([]);
  readonly error = signal('');
  readonly isLoading = signal(false);
  readonly busyActions = signal<Set<string>>(new Set());
  readonly previewState = signal<FilePreviewState | null>(null);

  constructor(private api: ApiService, private sanitizer: DomSanitizer) { void this.load(); }

  async load(): Promise<void> {
    this.isLoading.set(true);
    try { this.files.set(await this.api.listFiles()); this.error.set(''); }
    catch (error) { this.error.set(error instanceof Error ? error.message : 'Unable to load files.'); }
    finally { this.isLoading.set(false); }
  }

  async add(payload: CreateUserFilePayload): Promise<UserFile | null> {
    if (this.isBusy('add')) return null;

    this.setBusy('add', 'new', true);
    try {
      const created = await this.api.createFile(payload);
      this.files.update(files => [created, ...files]);
      this.error.set('');
      return created;
    } catch (error) {
      this.error.set(error instanceof Error ? error.message : 'Unable to upload file.');
      return null;
    } finally {
      this.setBusy('add', 'new', false);
    }
  }

  async save(file: UserFile): Promise<void> {
    if (this.isBusy('save', file.id)) return;

    this.setBusy('save', file.id, true);
    try {
      const saved = await this.api.updateFile(file);
      this.files.update(files => files.map(item => item.id === file.id ? saved : item));
      this.error.set('');
    } catch (error) {
      this.error.set(error instanceof Error ? error.message : 'Unable to save file.');
    } finally {
      this.setBusy('save', file.id, false);
    }
  }

  async replaceContent(file: UserFile, replacement: ReplaceUserFileContentPayload): Promise<void> {
    if (this.isBusy('upload', file.id)) return;

    this.setBusy('upload', file.id, true);
    try {
      const saved = await this.api.replaceFileContent(file, replacement);
      this.files.update(files => files.map(item => item.id === file.id ? saved : item));
      this.error.set('');
    } catch (error) {
      this.error.set(error instanceof Error ? error.message : 'Unable to update file content.');
    } finally {
      this.setBusy('upload', file.id, false);
    }
  }

  async remove(id: string): Promise<void> {
    if (this.isBusy('delete', id)) return;

    this.setBusy('delete', id, true);
    try {
      const file = this.files().find(item => item.id === id);
      if (!file?.isLocalOnly) await this.api.deleteFile(id);
      this.files.update(files => files.filter(file => file.id !== id));
      this.error.set('');
    } catch (error) {
      this.error.set(error instanceof Error ? error.message : 'Unable to delete file.');
    } finally {
      this.setBusy('delete', id, false);
    }
  }

  preview(file: UserFile): void {
    if (this.isBusy('preview', file.id)) return;

    this.setBusy('preview', file.id, true);
    void this.openDecryptedFile(file, true, 'preview');
  }

  download(file: UserFile): void {
    if (this.isBusy('download', file.id)) return;

    this.setBusy('download', file.id, true);
    void this.openDecryptedFile(file, false, 'download');
  }

  closePreview(): void {
    const preview = this.previewState();
    if (preview) URL.revokeObjectURL(preview.objectUrl);
    this.previewState.set(null);
  }

  downloadPreview(): void {
    const preview = this.previewState();
    if (!preview) return;

    const link = document.createElement('a');
    link.href = preview.objectUrl;
    link.download = preview.filename;
    link.click();
  }

  isBusy(action: FileAction, id = 'new'): boolean {
    return this.busyActions().has(this.busyKey(action, id));
  }

  private setBusy(action: FileAction, id: string, busy: boolean): void {
    this.busyActions.update(actions => {
      const next = new Set(actions);
      const key = this.busyKey(action, id);
      if (busy) next.add(key);
      else next.delete(key);
      return next;
    });
  }

  private busyKey(action: FileAction, id: string): string {
    return `${action}:${id}`;
  }

  private async openDecryptedFile(file: UserFile, preview: boolean, action: 'preview' | 'download'): Promise<void> {
    try {
      const content = await this.api.getFileContent(file.id);
      const bytes = new Uint8Array(this.base64ToBytes(content.contentBase64));
      const blob = new Blob([bytes], { type: content.contentType || file.contentType || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      if (preview) {
        this.closePreview();
        this.previewState.set({
          file,
          filename: content.filename || file.originalFilename || file.title || 'document',
          contentType: blob.type,
          objectUrl: url,
          safeUrl: this.sanitizer.bypassSecurityTrustResourceUrl(url),
          canEmbed: this.canEmbed(blob.type, content.filename || file.originalFilename || file.title)
        });
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = content.filename || file.originalFilename || file.title || 'document';
        link.click();
        URL.revokeObjectURL(url);
      }
      this.error.set('');
    } catch (error) {
      this.error.set(error instanceof Error ? error.message : 'Unable to retrieve file.');
    } finally {
      this.setBusy(action, file.id, false);
    }
  }

  private base64ToBytes(value: string): Uint8Array {
    const binary = window.atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return bytes;
  }

  private canEmbed(contentType: string, filename: string): boolean {
    const type = contentType.toLowerCase();
    const name = filename.toLowerCase();
    return type === 'application/pdf'
      || this.isPassiveImage(type, name)
      || this.isPlainText(type, name)
      || type === 'application/json'
      || /\.(pdf|png|jpe?g|gif|webp|txt|csv|json)$/.test(name);
  }

  private isPassiveImage(contentType: string, filename: string): boolean {
    return ['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(contentType)
      || /\.(png|jpe?g|gif|webp)$/.test(filename);
  }

  private isPlainText(contentType: string, filename: string): boolean {
    return ['text/plain', 'text/csv', 'text/markdown'].includes(contentType)
      || /\.(txt|csv|md)$/.test(filename);
  }
}
